import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronRight, Phone, MessageSquare, Loader2 } from "lucide-react";

const STAGES = [
  { key: "scraped", label: "Scraped", color: "#A1A1AA" },
  { key: "contacted", label: "Contacted", color: "#002FA7" },
  { key: "answered", label: "Answered", color: "#06B6D4" },
  { key: "qualified", label: "Qualified", color: "#F59E0B" },
  { key: "booked", label: "Booked", color: "#8B5CF6" },
  { key: "demo", label: "Demo", color: "#0A0A0A" },
  { key: "won", label: "Won", color: "#10B981" },
  { key: "lost", label: "Lost", color: "#EF4444" },
];

export default function Pipeline() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const moveStage = async (leadId, nextStage) => {
    try {
      await api.patch(`/leads/${leadId}/stage`, { stage: nextStage });
      toast.success(`Moved to ${nextStage}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const groupedByStage = (key) => leads.filter((l) => l.stage === key);

  return (
    <div className="p-8" data-testid="pipeline-page">
      <div className="border-b border-zinc-200 pb-6 mb-6">
        <div className="overline mb-2">/ crm pipeline</div>
        <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight">Pipeline</h1>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 font-mono">Loading…</div>
      ) : (
        <div className="flex gap-4 overflow-x-auto kanban-board pb-4">
          {STAGES.map((stage) => {
            const items = groupedByStage(stage.key);
            return (
              <div
                key={stage.key}
                className="min-w-[280px] w-[280px] flex flex-col border border-zinc-200 bg-white"
                data-testid={`column-${stage.key}`}
              >
                <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between" style={{ borderTop: `3px solid ${stage.color}` }}>
                  <div>
                    <div className="overline">{stage.label}</div>
                    <div className="font-heading text-2xl font-black mt-1">{items.length}</div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto max-h-[70vh] p-3 space-y-2">
                  {items.length === 0 && (
                    <div className="text-xs text-zinc-400 font-mono text-center py-6">empty</div>
                  )}
                  {items.map((lead) => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      stages={STAGES}
                      onMove={moveStage}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LeadCard({ lead, stages, onMove }) {
  return (
    <div className="border border-zinc-200 bg-white p-3 hover:shadow-sm transition-shadow" data-testid={`lead-card-${lead.id}`}>
      <div className="flex items-start justify-between mb-2">
        <Link
          to={`/leads/${lead.id}`}
          data-testid={`lead-card-open-${lead.id}`}
          className="font-semibold text-sm leading-tight hover:text-[#002FA7] hover:underline underline-offset-2 text-zinc-900"
        >
          {lead.name}
        </Link>
        {lead.ai_score != null && (
          <span
            className="px-1.5 py-0.5 text-[9px] font-bold uppercase shrink-0 ml-2"
            style={{
              background:
                lead.ai_label === "Hot" ? "#EF4444"
                : lead.ai_label === "Warm" ? "#F59E0B"
                : "#52525B",
              color: "white",
            }}
          >
            {lead.ai_label}
          </span>
        )}
      </div>
      <div className="text-xs text-zinc-500 mb-2 font-mono">{lead.city}{lead.state ? `, ${lead.state}` : ""}</div>
      {lead.ai_score != null && (
        <div className="text-[10px] uppercase tracking-wider text-zinc-600 mb-2">
          Score: <span className="font-mono font-bold text-zinc-900">{lead.ai_score}/100</span>
        </div>
      )}
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-zinc-100">
        <select
          value={lead.stage}
          onChange={(e) => onMove(lead.id, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          data-testid={`stage-select-${lead.id}`}
          className="text-[10px] uppercase font-bold tracking-wider border border-zinc-200 px-1 py-0.5 bg-white"
        >
          {stages.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
        <Link
          to={`/leads/${lead.id}`}
          data-testid={`open-${lead.id}`}
          className="text-[10px] uppercase font-bold tracking-wider text-[#002FA7] hover:text-[#00227A] flex items-center gap-0.5"
        >
          Open <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
